import { AlertTriangle, FileText, ListChecks } from "lucide-react";
import { formatAed, formatDate, formatNumber, useLocale, useTranslation, type Locale } from "@ncp/i18n";
import { Badge, Card, CardContent, CardHeader, CardTitle, InfoHint, SourceText, cn } from "@ncp/ui";
import type { EcFieldFlag, EcFieldSource, EcFormView, EcMoneyValue } from "./api";

/** The form's field names as the officer reads them; a share names its undertaking. */
const LABELS: Record<string, [string, string]> = {
  title: ["Short title", "العنوان المختصر"], transactionType: ["Type of concentration", "نوع التركز"], transactionDescription: ["Description of the transaction", "وصف الصفقة"],
  considerationAed: ["Consideration", "قيمة الصفقة"], signingDate: ["Signing date", "تاريخ التوقيع"], expectedClosingDate: ["Expected closing date", "تاريخ الإتمام المتوقع"],
  controlStructure: ["Control structure", "هيكل السيطرة"], combinedUaeTurnoverAed: ["Total annual sales in the State", "إجمالي المبيعات السنوية في الدولة"], fiscalYear: ["Fiscal year", "السنة المالية"],
  acquiredStakePercent: ["Shares / voting rights acquired", "الأسهم / حقوق التصويت المستحوذ عليها"], preTransactionStakePercent: ["Stake held before", "الحصة المملوكة قبل الصفقة"],
  largestOtherShareholderPercent: ["Largest other shareholder after closing", "أكبر مساهم آخر بعد الإتمام"], controlType: ["Control acquired", "السيطرة المكتسبة"], controlRights: ["Rights conferred", "الحقوق الممنوحة"],
  intraGroup: ["Intra-group restructuring", "إعادة هيكلة داخل المجموعة"], fullFunctionJointVenture: ["Full-function joint venture", "مشروع مشترك كامل الوظائف"], assetTransferIsBusiness: ["Assets form a business", "الأصول تشكل نشاطا تجاريا"],
  rationale: ["Commercial rationale", "المبررات التجارية"], efficienciesClaimed: ["Efficiencies claimed", "الكفاءات المدعاة"], otherJurisdictionsNotified: ["Other jurisdictions notified", "الجهات الأخرى المخطرة"],
  productMarket: ["Relevant product market", "السوق المعنية"], geographicMarket: ["Geographic market", "السوق الجغرافية"], isicCode: ["Activity code", "رمز النشاط"], marketSizeAed: ["Market size", "حجم السوق"],
};

/** The figures and dates: the fields whose source the officer checks first (text fields follow). */
const FIGURES = new Set(["considerationAed", "combinedUaeTurnoverAed", "fiscalYear", "signingDate", "expectedClosingDate", "acquiredStakePercent", "preTransactionStakePercent", "largestOtherShareholderPercent", "marketSizeAed"]);

function label(field: string, ar: boolean): string {
  if (field.startsWith("share:")) return ar ? `حصة ${field.slice(6)}` : `Market share of ${field.slice(6)}`;
  return LABELS[field]?.[ar ? 1 : 0] ?? field;
}

/** "USD 100,000,000 × 3.6725 (Central Bank of the UAE, 12 Mar 2026)": the amount as filed beside its AED figure. */
export function moneyText(m: EcMoneyValue | null | undefined, locale: Locale, publishedLabel: string): string | null {
  if (!m || m.currency === "AED") return null;
  const original = `${m.currency} ${formatNumber(m.originalAmount, locale)}`;
  if (m.rate == null) return original;
  const source = locale === "ar" ? m.rateSourceAr ?? m.rateSource : m.rateSource;
  return `${original} × ${formatNumber(m.rate, locale, { maximumFractionDigits: 6 })} (${source ?? "—"}${m.rateDate ? `, ${formatDate(m.rateDate, locale)}` : ""}${m.ratePublished ? `, ${publishedLabel}` : ""})`;
}

function displayValue(field: string, value: string, locale: Locale): string {
  const n = Number(value);
  if (/Aed$/.test(field) && Number.isFinite(n)) return formatAed(n, locale);
  if ((/Percent$/.test(field) || field.startsWith("share:")) && Number.isFinite(n)) return `${formatNumber(n, locale, { maximumFractionDigits: 2 })}%`;
  return value;
}

interface Row { key: string; field: string; value: string; source?: EcFieldSource | null; money?: EcMoneyValue | null; market?: string; flags: EcFieldFlag[] }

/**
 * Where each value on the notification comes from (S 2.4): its source beside it – a document on the file with page and passage, the
 * conversation, or the person who entered it – the flags the platform raises on it, and an amount filed in another currency with its rate,
 * source and date (S 6.4).
 */
export function FiledValuesCard({ form }: { form: EcFormView }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const flags = form.fieldFlags ?? [];
  const rows: Row[] = [];
  for (const [field, source] of Object.entries(form.sources ?? {}))
    rows.push({ key: field, field, value: source.value, source, money: field === "considerationAed" ? form.consideration : field === "combinedUaeTurnoverAed" ? form.turnover : null, flags: flags.filter((f) => f.field === field && !f.marketId) });
  for (const m of form.markets)
    for (const [field, source] of Object.entries(m.sources ?? {}))
      rows.push({ key: `${m.id}:${field}`, field, value: source.value, source, money: field === "marketSizeAed" ? m.marketSize : null, market: ar && m.productMarketAr ? m.productMarketAr : m.productMarket, flags: flags.filter((f) => f.field === field && f.marketId === m.id) });
  // A flagged value without a recorded source (or an amount awaiting its rate) still gets its row.
  for (const f of flags)
    if (!rows.some((r) => r.field === f.field && (f.marketId ? r.key.startsWith(`${f.marketId}:`) : !r.market)))
      rows.push({ key: `${f.marketId ?? ""}:${f.field}:${f.code}`, field: f.field, value: "", flags: [f], market: f.marketId ? form.markets.find((m) => m.id === f.marketId)?.productMarket : undefined });
  rows.sort((a, b) => Number(FIGURES.has(b.field) || b.field.startsWith("share:")) - Number(FIGURES.has(a.field) || a.field.startsWith("share:")) || Number(b.flags.length > 0) - Number(a.flags.length > 0));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><ListChecks className="size-4 text-aegold-700" /> {t("backoffice:filedValues.title")}<InfoHint>{t("backoffice:filedValues.hint")}</InfoHint>
          {flags.length ? <Badge size="sm" variant="warning" className="ms-auto"><AlertTriangle /> {flags.length}</Badge> : null}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? <p className="text-sm text-muted-foreground">{t("backoffice:filedValues.none")}</p> : (
          <ul className="divide-y divide-sand-100 text-sm">
            {rows.map((r) => {
              const original = moneyText(r.money, locale, t("backoffice:filedValues.published"));
              return (
                <li key={r.key} className="grid gap-1 py-2 sm:grid-cols-[minmax(10rem,16rem)_minmax(0,1fr)] sm:gap-4">
                  <div className="min-w-0">
                    <p className="font-medium">{label(r.field, ar)}</p>
                    {r.market ? <p className="truncate text-xs text-muted-foreground">{t("backoffice:filedValues.market", { market: r.market })}</p> : null}
                  </div>
                  <div className="min-w-0 space-y-1">
                    {r.value ? <p className="tabular" dir="auto">{FIGURES.has(r.field) || r.field.startsWith("share:") ? displayValue(r.field, r.value, locale) : <SourceText text={r.value.length > 160 ? `${r.value.slice(0, 160)}…` : r.value} />}</p> : null}
                    {original ? <p className="text-xs text-sand-800">{t("backoffice:filedValues.originalAmount", { original })}</p> : null}
                    <p className={cn("text-xs", r.source ? "text-muted-foreground" : "text-warning-800")}>
                      {r.source ? (
                        <>
                          <FileText className="me-1 inline size-3 -translate-y-px" />
                          {t(`backoffice:filedValues.kinds.${r.source.kind}`)}
                          {r.source.documentName ? ` · ${r.source.documentName}` : ""}
                          {r.source.page ? ` · ${t("backoffice:filedValues.page", { page: r.source.page })}` : ""}
                          {r.source.excerpt ? <> · “<SourceText text={r.source.excerpt} />”</> : null}
                          {r.source.passageFound === false ? ` · ${t("backoffice:filedValues.passageNotFound")}` : ""}
                          {r.source.passageStatesValue === false ? ` · ${t("backoffice:filedValues.passageDoesNotState")}` : ""}
                        </>
                      ) : t("backoffice:filedValues.noSource")}
                    </p>
                    {r.flags.map((f) => <p key={f.code} className="flex items-start gap-1.5 text-xs text-warning-900"><AlertTriangle className="mt-0.5 size-3 shrink-0" /><span>{ar ? f.ar : f.en}</span></p>)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
